/**
 * 管理后台路由(Vue Router)
 * 基于 pagePermissions 做统一菜单/路由鉴权
 */

import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
  type RouteRecordRaw,
} from 'vue-router';
import {
  useAuthStore,
  type AdminPagePermission,
} from '@/stores/auth';

interface AdminRouteMeta {
  requiresAuth?: boolean;
  title?: string;
  requiredPermissions?: AdminPagePermission[];
}

const routes: RouteRecordRaw[] = [
  { path: '/', redirect: '/login' },
  { path: '/login', component: () => import('@/pages/Login.vue') },
  {
    path: '/admin',
    component: () => import('@/layouts/AdminLayout.vue'),
    meta: { requiresAuth: true } satisfies AdminRouteMeta,
    children: [
      { path: '', redirect: '/admin/dashboard' },
      {
        path: 'dashboard',
        component: () => import('@/pages/Dashboard.vue'),
        meta: {
          title: '概览',
          requiredPermissions: ['dashboard'],
        } satisfies AdminRouteMeta,
      },
      {
        path: 'associations',
        component: () => import('@/pages/Associations.vue'),
        meta: {
          title: '学会管理',
          requiredPermissions: ['associations'],
        } satisfies AdminRouteMeta,
      },
      {
        path: 'talents',
        component: () => import('@/pages/Talents.vue'),
        meta: {
          title: '人才管理',
          requiredPermissions: ['talents'],
        } satisfies AdminRouteMeta,
      },
      {
        path: 'companies',
        component: () => import('@/pages/Companies.vue'),
        meta: {
          title: '企业管理',
          requiredPermissions: ['companies'],
        } satisfies AdminRouteMeta,
      },
      {
        path: 'projects',
        component: () => import('@/pages/Projects.vue'),
        meta: {
          title: '项目管理',
          requiredPermissions: ['projects'],
        } satisfies AdminRouteMeta,
      },
      {
        path: 'applications',
        component: () => import('@/pages/Applications.vue'),
        meta: {
          title: '报名管理',
          requiredPermissions: ['applications'],
        } satisfies AdminRouteMeta,
      },
      {
        path: 'invitations',
        component: () => import('@/pages/Invitations.vue'),
        meta: {
          title: '邀约管理',
          requiredPermissions: ['invitations'],
        } satisfies AdminRouteMeta,
      },
      {
        path: 'dictionaries',
        component: () => import('@/pages/Dictionaries.vue'),
        meta: {
          title: '字典管理',
          requiredPermissions: ['dictionaries'],
        } satisfies AdminRouteMeta,
      },
      {
        path: 'banners',
        component: () => import('@/pages/Banners.vue'),
        meta: {
          title: '轮播图',
          requiredPermissions: ['banners'],
        } satisfies AdminRouteMeta,
      },
      {
        path: 'sub-accounts',
        component: () => import('@/pages/SubAccounts.vue'),
        meta: {
          title: '学会账号管理',
          requiredPermissions: ['associationAccounts', 'subAccounts'],
        } satisfies AdminRouteMeta,
      },
      {
        path: 'wechat-users',
        component: () => import('@/pages/WechatUsers.vue'),
        meta: {
          title: '微信用户',
          requiredPermissions: ['wechatUsers'],
        } satisfies AdminRouteMeta,
      },
      {
        path: 'change-password',
        component: () => import('@/pages/ChangePassword.vue'),
        meta: {
          title: '修改密码',
        } satisfies AdminRouteMeta,
      },
    ],
  },
  { path: '/:pathMatch(.*)*', redirect: '/login' },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

function resolveRoutePermissions(to: RouteLocationNormalized): AdminPagePermission[] {
  const permissions: AdminPagePermission[] = [];

  to.matched.forEach((record) => {
    const meta = record.meta as AdminRouteMeta;
    (meta.requiredPermissions ?? []).forEach((permission) => {
      if (!permissions.includes(permission)) {
        permissions.push(permission);
      }
    });
  });

  return permissions;
}

router.beforeEach((to) => {
  const auth = useAuthStore();
  const fallbackPath = auth.defaultAdminPath;

  if ((to.meta as AdminRouteMeta).requiresAuth && !auth.isLoggedIn) {
    return { path: '/login', query: { redirect: to.fullPath } };
  }

  if (to.path === '/login' && auth.isLoggedIn) {
    return { path: fallbackPath };
  }

  if (to.path === '/admin' || to.path === '/admin/') {
    return { path: fallbackPath };
  }

  const requiredPermissions = resolveRoutePermissions(to);
  if (requiredPermissions.length > 0 && !auth.hasAnyPagePermission(requiredPermissions)) {
    return { path: fallbackPath === to.path ? '/admin/change-password' : fallbackPath };
  }

  return true;
});
